import { __read, __spreadArray } from "tslib";
import { FontIcon } from '@fluentui/react';
import { MenuItemTypes } from '@office-start/common-types';
import { DropdownMenu } from '@office-start/dropdown-menu';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import FluentPillControl from '@office-start/fluent-pill-control';
import { useMemoizedState } from '@office-start/hooks-utility';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import PillControl from '@office-start/pill-control';
import { format } from '@office-start/string-formatter-utility';
import { useSingletonWindowSize } from '@office-start/window-utility';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import styles from './PivotsControl.module.scss';
export var PivotsOverflow;
(function (PivotsOverflow) {
    PivotsOverflow[PivotsOverflow["None"] = 0] = "None";
    PivotsOverflow[PivotsOverflow["Dropdown"] = 1] = "Dropdown";
})(PivotsOverflow || (PivotsOverflow = {}));
var supportedHorizontalKeys = new Set([
    KeyboardKey.Enter,
    KeyboardKey.Space,
    KeyboardKey.ArrowLeft,
    KeyboardKey.ArrowRight,
]);
var supportedVerticalKeys = new Set([
    KeyboardKey.Enter,
    KeyboardKey.Space,
    KeyboardKey.ArrowUp,
    KeyboardKey.ArrowDown,
]);
export var PivotsControl = function (_a) {
    var _b;
    var onSelectTab = _a.onSelectTab, tabs = _a.tabs, _c = _a.pivotsOverflow, pivotsOverflow = _c === void 0 ? PivotsOverflow.None : _c, dropdownButtonText = _a.dropdownButtonText, isVertical = _a.isVertical, showIcons = _a.showIcons, activeTabId = _a.activeTabId, maxPivotCount = _a.maxPivotCount, _d = _a.showPills, showPills = _d === void 0 ? false : _d, _f = _a.onlyDropdownShownLeftAlignContext, onlyDropdownShownLeftAlignContext = _f === void 0 ? false : _f, onAddPendingPivot = _a.onAddPendingPivot, onRemovePivot = _a.onRemovePivot, pendingCustomPivotId = _a.pendingCustomPivotId, addCustomPivotButtonText = _a.addCustomPivotButtonText, removeNewCustomPivotButtonText = _a.removeNewCustomPivotButtonText, removeNamedCustomPivotButtonText = _a.removeNamedCustomPivotButtonText, persistTabOrder = _a.persistTabOrder, selectedTabInDropdownAriaLabel = _a.selectedTabInDropdownAriaLabel, isFluentPill = _a.isFluentPill;
    var supportedKeys = isVertical
        ? supportedVerticalKeys
        : supportedHorizontalKeys;
    var isRtl = document.dir === 'rtl';
    var addPendingPivotButtonId = 'add-pending-pivot-button';
    //Init values
    var displayDropdown = pivotsOverflow === PivotsOverflow.Dropdown && tabs.length > 1;
    //Pivot Control States
    var _g = __read(useMemoizedState(tabs), 2), pivots = _g[0], setPivots = _g[1];
    var defaultTabState = tabs && tabs.length
        ? activeTabId && !tabs.some(function (tab) { return tab.id === activeTabId; })
            ? activeTabId
            : tabs[0].id
        : '';
    var _h = __read(useMemoizedState(defaultTabState), 2), selectedTabId = _h[0], setSelectedTabId = _h[1];
    var _j = __read(useState(defaultTabState), 2), focusTabId = _j[0], setFocusTabId = _j[1];
    var _k = __read(useState(defaultTabState), 2), prevSelectedTabId = _k[0], setPrevSelectedTabId = _k[1];
    var _l = __read(useState(false), 2), reordered = _l[0], setReordered = _l[1];
    var _m = __read(useState(0), 2), containerWidth = _m[0], setContainerWidth = _m[1];
    var _o = __read(useMemoizedState(tabs.length), 2), overflowIndex = _o[0], setOverflowIndex = _o[1];
    var _p = __read(useState(0), 2), dropdownWidth = _p[0], setDropdownWidth = _p[1];
    var _q = __read(useState(false), 2), selectedTabInDropdown = _q[0], setSelectedTabInDropdown = _q[1];
    //Pivot Control Refs
    var containerRef = useRef(null);
    var dropdownPivotRef = useRef(null);
    var dropdownButtonRef = useRef(null);
    var pivotWidthMapRef = useRef(new Map());
    var pivotRefs = useRef([]);
    var addPivotRef = useRef(null);
    var width = useSingletonWindowSize().width;
    //Pivot onClick handler
    var setSelected = useCallback(function (key) {
        setSelectedTabId(key);
        onSelectTab && onSelectTab({ tabId: key });
        setFocusTabId(key);
        if (key !== pendingCustomPivotId) {
            setPrevSelectedTabId(key);
        }
    }, [
        onSelectTab,
        pendingCustomPivotId,
        setPrevSelectedTabId,
        setSelectedTabId,
    ]);
    var dropdownItems = useMemo(function () {
        return displayDropdown
            ? pivots &&
                pivots.slice(overflowIndex).map(function (pivot) {
                    var iconElement = undefined;
                    var ariaLabel = undefined;
                    var activeIconElement = pivot.iconElement && pivot.iconElementActive ? (pivot.iconElementActive) : (React.createElement(FontIcon, { iconName: IconNames.CheckMark }));
                    if ((persistTabOrder || overflowIndex === 0) &&
                        selectedTabInDropdown &&
                        pivot.id === selectedTabId) {
                        iconElement = activeIconElement;
                        if (selectedTabInDropdownAriaLabel) {
                            ariaLabel = format(selectedTabInDropdownAriaLabel, pivot.title);
                        }
                    }
                    else if (pivot.iconElement && pivot.iconElementActive) {
                        iconElement = pivot.iconElement;
                    }
                    return {
                        key: pivot.id,
                        content: pivot.title,
                        iconElement: iconElement,
                        ariaLabel: ariaLabel,
                    };
                })
            : [];
    }, [
        displayDropdown,
        overflowIndex,
        persistTabOrder,
        pivots,
        selectedTabId,
        selectedTabInDropdown,
        selectedTabInDropdownAriaLabel,
    ]);
    var wrappedOnRemovePivot = useCallback(function (pivotToRemove) {
        if (onRemovePivot) {
            setSelected(prevSelectedTabId === pivotToRemove.id
                ? 'all'
                : prevSelectedTabId);
            onRemovePivot(pivotToRemove);
        }
    }, [onRemovePivot, prevSelectedTabId, setSelected]);
    var wrappedOnAddPendingPivot = useCallback(function () {
        if (onAddPendingPivot) {
            onAddPendingPivot();
        }
        if (pendingCustomPivotId) {
            setSelected(pendingCustomPivotId);
        }
    }, [onAddPendingPivot, pendingCustomPivotId, setSelected]);
    var movesFocus = useCallback(function (tabId) {
        var _a, _b, _c;
        var indexInTabsArray = tabs.findIndex(function (pivot) { return tabId === pivot.id; });
        var isAddPivot = tabId === addPendingPivotButtonId;
        if (!isAddPivot && displayDropdown && indexInTabsArray === -1) {
            (_a = dropdownButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (isAddPivot) {
            (_b = addPivotRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        else if (indexInTabsArray < pivots.length) {
            (_c = pivotRefs.current[indexInTabsArray]) === null || _c === void 0 ? void 0 : _c.focus();
        }
    }, [tabs, displayDropdown, pivots.length]);
    var handlePivotKeyDown = useCallback(function (event, pivotKey) {
        var keyboardKey = getKey(event);
        if (!supportedKeys.has(keyboardKey)) {
            return;
        }
        var limit = displayDropdown ? overflowIndex : tabs.length;
        var indexInPivotsArray = pivots.findIndex(function (pivot) { return pivotKey === pivot.id; });
        var tabId;
        event.preventDefault();
        var moveForward = function () {
            if (onAddPendingPivot && indexInPivotsArray + 1 === limit) {
                tabId = addPendingPivotButtonId;
            }
            else {
                tabId = pivots[(indexInPivotsArray + 1) % limit].id;
            }
            movesFocus(tabId);
        };
        var moveBackward = function () {
            if (indexInPivotsArray === 0 && onAddPendingPivot) {
                tabId = addPendingPivotButtonId;
            }
            else if (pivotKey === addPendingPivotButtonId) {
                tabId =
                    displayDropdown &&
                        dropdownItems.length &&
                        indexInPivotsArray - 1 < 0
                        ? ''
                        : pivots[(indexInPivotsArray + limit) % limit].id;
            }
            else {
                tabId = pivots[(indexInPivotsArray - 1 + limit) % limit].id;
            }
            movesFocus(tabId);
        };
        switch (keyboardKey) {
            case KeyboardKey.Enter:
            case KeyboardKey.Space:
                if (pivotKey === addPendingPivotButtonId) {
                    wrappedOnAddPendingPivot();
                }
                else {
                    setSelected(pivotKey);
                }
                break;
            case KeyboardKey.ArrowLeft:
                if (isRtl) {
                    moveForward();
                    break;
                }
            case KeyboardKey.ArrowUp:
                moveBackward();
                break;
            case KeyboardKey.ArrowRight:
                if (isRtl) {
                    moveBackward();
                    break;
                }
            case KeyboardKey.ArrowDown:
                moveForward();
                break;
        }
    }, 
    // eslint-disable-next-line  react-hooks/exhaustive-deps
    [
        supportedKeys,
        displayDropdown,
        isRtl,
        overflowIndex,
        tabs.length,
        pivots,
        onAddPendingPivot,
        dropdownItems.length,
        wrappedOnAddPendingPivot,
        setSelected,
    ]);
    var handleDropdownKey = useCallback(function (event) {
        var keyboardKey = getKey(event);
        if (!supportedKeys.has(keyboardKey)) {
            return;
        }
        var tabId;
        event.preventDefault();
        var moveForward = function () {
            if (onAddPendingPivot) {
                tabId = addPendingPivotButtonId;
            }
            else {
                tabId = pivots[0].id;
            }
            setFocusTabId(tabId);
            movesFocus(tabId);
        };
        var moveBackward = function () {
            tabId = pivots[overflowIndex - 1].id;
            setFocusTabId(tabId);
            movesFocus(tabId);
        };
        switch (keyboardKey) {
            case KeyboardKey.ArrowLeft:
                isRtl ? moveForward() : moveBackward();
                break;
            case KeyboardKey.ArrowRight:
                isRtl ? moveBackward() : moveForward();
                break;
        }
    }, [
        isRtl,
        onAddPendingPivot,
        overflowIndex,
        pivots,
        supportedKeys,
        movesFocus,
    ]);
    var handleOrderPersistedOverflow = useCallback(function (tabId) {
        var curTabId = tabId !== null && tabId !== void 0 ? tabId : selectedTabId;
        setSelectedTabId(curTabId);
        var selectedTabIndex = pivots.findIndex(function (pivot) { return curTabId === pivot.id; });
        var pivotWidthMap = pivotWidthMapRef.current;
        var pivotOffset = 0;
        for (var i = 0; i < tabs.length; i++) {
            var ithPivotWidth = pivotWidthMap.get(tabs[i].id);
            if (ithPivotWidth) {
                pivotOffset += ithPivotWidth;
                var currentWidth = i === tabs.length - 1
                    ? pivotOffset
                    : pivotOffset + dropdownWidth;
                if (currentWidth > containerWidth ||
                    (maxPivotCount && i === maxPivotCount)) {
                    setOverflowIndex(i);
                    if (selectedTabIndex >= i) {
                        setSelectedTabInDropdown(true);
                    }
                    else {
                        setSelectedTabInDropdown(false);
                    }
                    return;
                }
            }
        }
        setSelectedTabInDropdown(false);
        setOverflowIndex(pivots.length);
    }, [
        containerWidth,
        dropdownWidth,
        pivots,
        selectedTabId,
        maxPivotCount,
        setOverflowIndex,
        setSelectedTabId,
        tabs,
    ]);
    // Computes the pivot index where overflow starts
    var handleOverflow = useDebouncedCallback(function (tabId) {
        if (!displayDropdown ||
            !containerRef.current ||
            !pivotWidthMapRef.current ||
            pivotWidthMapRef.current.size === 0) {
            return;
        }
        if (persistTabOrder) {
            return handleOrderPersistedOverflow(tabId);
        }
        // If dropdown, insert the selected dropdown item at "last definitely visible index"
        // Swapping can change overflow index and can result in pushing more pivots in dropdown
        var deleteIndex = undefined;
        if (tabId) {
            deleteIndex = pivots.findIndex(function (pivot) { return tabId === pivot.id; });
            setSelectedTabId(pivots[deleteIndex].id);
        }
        var insertIndex = deleteIndex;
        var pivotWidthMap = pivotWidthMapRef.current;
        var selectedPivotWidth = pivotWidthMap.get(selectedTabId);
        var prevPivotOffset = selectedPivotWidth ? selectedPivotWidth : 0;
        var selectedTabFound = false;
        for (var i = 0; i < pivots.length; i++) {
            var currentPivotOffset = void 0;
            if (pivots[i].id != selectedTabId) {
                currentPivotOffset =
                    prevPivotOffset + (pivotWidthMap.get(pivots[i].id) || 0);
            }
            else {
                currentPivotOffset = prevPivotOffset;
                selectedTabFound = true;
            }
            prevPivotOffset = currentPivotOffset;
            var currentWidth = i < pivots.length - 1
                ? currentPivotOffset + dropdownWidth
                : currentPivotOffset;
            if (currentWidth >= containerWidth ||
                (maxPivotCount && i === maxPivotCount)) {
                insertIndex = i - 1;
                if (deleteIndex !== undefined &&
                    insertIndex !== undefined &&
                    insertIndex !== deleteIndex) {
                    //Delete the selected pivot from it's current position and insert it at insertIndex
                    var pivotsTemp = pivots.slice();
                    pivotsTemp.splice(deleteIndex, 1);
                    pivotsTemp.splice(insertIndex, 0, pivots[deleteIndex]);
                    setPivots(pivotsTemp);
                    setReordered(true);
                    handleOverflow();
                }
                else if (!selectedTabFound) {
                    var selectedIndex = pivots.findIndex(function (pivot) {
                        return pivot.id === selectedTabId;
                    });
                    var temp = pivots[selectedIndex];
                    pivots.splice(selectedIndex, 1);
                    pivots.splice(i - 1, 0, temp);
                }
                setOverflowIndex(i);
                return;
            }
        }
        setOverflowIndex(pivots.length);
    }, 50);
    // Dropdown onKeySelected handler
    // Behavior: If item from dropdown is selected, it is replaced with the last visible item in pivots and remaining items are pushed back in the dropdown
    var handleDropdownItemSelected = useCallback(function (key, _e, isSelected) {
        var _a;
        //selected key within the dropdown cannot be reset when pivot outside of dropdown is clicked, so ignore isSelected flag for persistOrder
        if (persistTabOrder) {
            setSelectedTabId(key);
            (_a = dropdownButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            onSelectTab && onSelectTab({ tabId: key });
            setSelectedTabInDropdown(true);
            return;
        }
        if (!isSelected) {
            return;
        }
        setSelectedTabId(key);
        setFocusTabId(key);
        onSelectTab && onSelectTab({ tabId: key });
        handleOverflow(key);
        setReordered(true);
    }, [setSelectedTabId, onSelectTab, handleOverflow, persistTabOrder]);
    var pivotTitles = useMemo(function () {
        var sliceIndex = maxPivotCount && maxPivotCount < overflowIndex
            ? maxPivotCount
            : overflowIndex;
        return pivots && pivots.length
            ? pivots.slice(0, sliceIndex).map(function (pivot) {
                var _a, _b;
                var _c, _d, _f, _g;
                var pivotElementId = pivot.id + '-pivot';
                return (React.createElement("span", { key: pivot.id, className: classNames((_a = {},
                        _a[styles['tab-list__header__title']] = !showPills,
                        _a[styles['tab-list__header__title--vertical']] = isVertical,
                        _a[styles['tab-list__header__title--selected']] = pivot.id === selectedTabId && isVertical,
                        _a[styles['tab-list__header__title-text--vertical-selected']] = pivot.id === selectedTabId && isVertical,
                        _a[styles['tab-list__fluentOutline']] = isFluentPill,
                        _a)), onClick: function () {
                        setSelected(pivot.id);
                        if (persistTabOrder) {
                            setSelectedTabInDropdown(false);
                        }
                    }, onKeyDown: function (event) {
                        return handlePivotKeyDown(event, pivot.id);
                    }, ref: function (el) {
                        var _a;
                        (_a = pivot.onPivotRefChange) === null || _a === void 0 ? void 0 : _a.call(pivot, el);
                        pivotRefs.current[tabs.findIndex(function (tab) { return tab.id === pivot.id; })] = el;
                        var pivotOffsetWidth = (el === null || el === void 0 ? void 0 : el.offsetWidth) || 0;
                        if ((displayDropdown &&
                            !pivotWidthMapRef.current.has(pivot.id)) ||
                            (pivotWidthMapRef.current.has(pivot.id) &&
                                pivotWidthMapRef.current.get(pivot.id) !=
                                    pivotOffsetWidth &&
                                pivotOffsetWidth != 0)) {
                            pivotWidthMapRef.current.set(pivot.id, pivotOffsetWidth);
                            handleOverflow();
                        }
                        if (reordered) {
                            if (focusTabId === pivot.id) {
                                el === null || el === void 0 ? void 0 : el.focus();
                                setReordered(false);
                            }
                        }
                    }, tabIndex: (isVertical &&
                        pivot.id === pivots[0].id &&
                        selectedTabId == '') || // First pivot in vertical pivot group and no currently active pivot in group
                        selectedTabId === pivot.id ||
                        focusTabId === pivot.id
                        ? 0
                        : -1, "aria-labelledby": pivotElementId, role: isVertical ? 'menuitemradio' : 'tab', "aria-selected": pivot.id === selectedTabId, "aria-checked": isVertical && pivot.id === selectedTabId }, showPills ? (pivot.isCustomPivot && onRemovePivot ? (React.createElement(PillControl, { id: pivotElementId, title: pivot.title, iconName: pivot.iconName, iconElement: pivot.iconElement, ariaLabel: (_c = pivot.ariaLabel) !== null && _c !== void 0 ? _c : pivot.title, isActive: pivot.id === selectedTabId, isRemovable: true, onRemoveClick: function () {
                        return wrappedOnRemovePivot(pivot);
                    }, disableFocus: true, removePillAriaLabel: pivot.id === pendingCustomPivotId
                        ? removeNewCustomPivotButtonText
                        : format(removeNamedCustomPivotButtonText !== null && removeNamedCustomPivotButtonText !== void 0 ? removeNamedCustomPivotButtonText : '', (_d = pivot.title) !== null && _d !== void 0 ? _d : '') })) : isFluentPill ? (React.createElement(FluentPillControl, { id: pivotElementId, title: pivot.title, isActive: pivot.id === selectedTabId, iconElement: !(pivot.iconElement instanceof
                        Promise)
                        ? pivot.iconElement
                        : undefined, iconElementRight: pivot.iconElementRight })) : (React.createElement(PillControl, { id: pivotElementId, title: pivot.title, iconName: pivot.iconName, ariaLabel: (_f = pivot.ariaLabel) !== null && _f !== void 0 ? _f : pivot.title, isActive: pivot.id === selectedTabId, iconElement: pivot.iconElement, iconElementActive: pivot.iconElementActive, disableFocus: true }))) : (React.createElement("div", { id: pivotElementId, title: pivot.title, "aria-label": (_g = pivot.ariaLabel) !== null && _g !== void 0 ? _g : pivot.title, className: classNames(styles['tab-list__header__title-text'], (_b = {},
                        _b[styles['tab-list__header__title-text--selected']] = pivot.id === selectedTabId &&
                            !isVertical,
                        _b[styles['tab-list__header__title-text--vertical']] = isVertical,
                        _b['ms-fcl-nd ms-bcl-tda'] = pivot.id === selectedTabId &&
                            !isVertical,
                        _b[styles['tab-list__header__title-text--vertical-selected']] = pivot.id === selectedTabId &&
                            isVertical,
                        _b)) },
                    showIcons && pivot.icon ? (React.createElement("div", { className: styles['tab-list__header__tab-icon'] }, pivot.icon)) : null,
                    showIcons && pivot.iconName ? (React.createElement(FontIcon, { className: styles['tab-list__header__tab-icon'], iconName: pivot.iconName })) : null,
                    showIcons && pivot.iconClassName ? (React.createElement("span", { className: pivot.iconClassName })) : null,
                    pivot.title))));
            })
            : null;
    }, [
        maxPivotCount,
        overflowIndex,
        pivots,
        showPills,
        isVertical,
        selectedTabId,
        isFluentPill,
        focusTabId,
        onRemovePivot,
        pendingCustomPivotId,
        removeNewCustomPivotButtonText,
        removeNamedCustomPivotButtonText,
        showIcons,
        setSelected,
        persistTabOrder,
        handlePivotKeyDown,
        tabs,
        displayDropdown,
        reordered,
        handleOverflow,
        wrappedOnRemovePivot,
    ]);
    useEffect(function () {
        if (displayDropdown && pivots.length === dropdownItems.length) {
            setSelectedTabInDropdown(true);
        }
        else if (activeTabId) {
            if (!tabs.some(function (tab) { return tab.id === activeTabId; })) {
                setSelectedTabId('');
            }
            else {
                if (dropdownItems.find(function (item) { return item.key === activeTabId; })) {
                    if (persistTabOrder) {
                        setSelectedTabInDropdown(true);
                    }
                    else {
                        setOverflowIndex(pivots.length);
                        handleOverflow(activeTabId);
                    }
                }
                else {
                    setSelectedTabInDropdown(false);
                }
                setFocusTabId(activeTabId);
                setSelectedTabId(activeTabId);
            }
        }
    }, [
        activeTabId,
        displayDropdown,
        dropdownItems,
        handleOverflow,
        persistTabOrder,
        pivots.length,
        setOverflowIndex,
        setSelectedTabId,
        setSelectedTabInDropdown,
        tabs,
    ]);
    useEffect(function () {
        var _a, _b;
        if (displayDropdown) {
            if (!dropdownWidth) {
                setDropdownWidth(((_a = dropdownPivotRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0);
            }
            var tempContainerWidth = ((_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0;
            // If custom pivot is enabled, decrease the width of plus button from container width.
            if (onAddPendingPivot) {
                tempContainerWidth =
                    tempContainerWidth >= 32 ? tempContainerWidth - 32 : 0;
            }
            setContainerWidth(tempContainerWidth);
            handleOverflow();
        }
    }, [
        displayDropdown,
        dropdownWidth,
        handleOverflow,
        onAddPendingPivot,
        width,
    ]);
    useEffect(function () {
        // Remove pivot from map if it's deleted from tabs array
        if (tabs.length !== pivotWidthMapRef.current.size) {
            var pivotMap = pivotWidthMapRef.current;
            var tabIds_1 = new Set(tabs.map(function (tab) { return tab.id; }));
            var deletedTabId = __spreadArray([], __read(pivotMap.keys()), false).find(function (key) { return !tabIds_1.has(key); });
            if (deletedTabId) {
                pivotMap.delete(deletedTabId);
            }
            handleOverflow();
        }
    }, [handleOverflow, tabs]);
    // Re-render when tabs are changed
    useEffect(function () {
        if (pivots && tabs) {
            var pivotsNeedUpdate = void 0;
            if (pivots.length !== tabs.length) {
                pivotsNeedUpdate = true;
            }
            else {
                var sortedPivotIds = pivots
                    .map(function (x) { return x.id; })
                    .sort();
                var sortedTabIds_1 = tabs.map(function (x) { return x.id; }).sort();
                pivotsNeedUpdate = !sortedPivotIds.every(function (x, i) { return x === sortedTabIds_1[i]; });
            }
            if (pivotsNeedUpdate) {
                setPivots(tabs);
                setSelectedTabId(tabs && tabs.length ? tabs[0].id : '');
                handleOverflow();
            }
        }
    }, [handleOverflow, pivots, setPivots, setSelectedTabId, tabs]);
    var dropdown = useMemo(function () {
        var _a, _b;
        var formattedDropdownButtonText = dropdownButtonText
            ? format(dropdownButtonText, dropdownItems.length.toString())
            : undefined;
        return displayDropdown ? (React.createElement("span", { ref: dropdownPivotRef, className: classNames(styles['tab-list__dropdown'], (_a = {},
                _a[styles['tab-list__dropdown--hidden']] = dropdownWidth === 0,
                _a[styles['tab-list__dropdown--none']] = dropdownWidth !== 0 && dropdownItems.length === 0,
                _a)), onKeyDown: handleDropdownKey },
            React.createElement(DropdownMenu, { buttonContent: showPills ? (isFluentPill ? (React.createElement(FluentPillControl, { title: formattedDropdownButtonText !== null && formattedDropdownButtonText !== void 0 ? formattedDropdownButtonText : '', isActive: persistTabOrder && selectedTabInDropdown, isDropdown: true })) : (React.createElement(PillControl, { title: formattedDropdownButtonText !== null && formattedDropdownButtonText !== void 0 ? formattedDropdownButtonText : '', isActive: persistTabOrder && selectedTabInDropdown, isDropdown: true, disableFocus: true }))) : (React.createElement("span", { className: classNames(styles['tab-list__header__title'], (_b = {},
                        _b[styles['tab-list__header__title--selected-underline']] = persistTabOrder &&
                            selectedTabInDropdown,
                        _b['ms-fcl-nd ms-bcl-tda'] = persistTabOrder &&
                            selectedTabInDropdown,
                        _b)) },
                    formattedDropdownButtonText,
                    React.createElement(FontIcon, { className: classNames('ms-fcl-tda', styles['tab-list__more__icon']), iconName: IconNames.ChevronDown }))), items: dropdownItems, options: {
                    leftAligned: onlyDropdownShownLeftAlignContext &&
                        overflowIndex === 0,
                }, menuItemType: MenuItemTypes.List, onKeySelected: handleDropdownItemSelected, stopButtonEventPropagation: true, ref: dropdownButtonRef, buttonAriaLabel: formattedDropdownButtonText, disableFocus: false }))) : null;
    }, [
        dropdownButtonText,
        dropdownItems,
        displayDropdown,
        dropdownWidth,
        handleDropdownKey,
        showPills,
        persistTabOrder,
        selectedTabInDropdown,
        isFluentPill,
        onlyDropdownShownLeftAlignContext,
        overflowIndex,
        handleDropdownItemSelected,
    ]);
    return (React.createElement("div", { ref: containerRef, className: classNames(styles['tab-list__header__tabs'], (_b = {},
            _b[styles['tab-list__header__tabs--vertical']] = isVertical,
            _b[styles['tab-list__header__pills']] = showPills && !isFluentPill,
            _b[styles['tab-list__header__fluentpills']] = isFluentPill,
            _b)), role: isVertical ? 'menu' : 'tablist' },
        pivotTitles,
        dropdown,
        onAddPendingPivot &&
            addCustomPivotButtonText &&
            !(tabs && tabs.find(function (tab) { return tab.id === pendingCustomPivotId; })) ? (React.createElement("span", { key: addPendingPivotButtonId, role: "tab", tabIndex: focusTabId === pendingCustomPivotId ? 0 : -1, ref: addPivotRef, onClick: wrappedOnAddPendingPivot, title: addCustomPivotButtonText, "aria-label": addCustomPivotButtonText, onKeyDown: function (event) {
                return handlePivotKeyDown(event, addPendingPivotButtonId);
            } },
            React.createElement(FontIcon, { iconName: IconNames.Add, className: styles['tab-list__header__add-tab-button'] }))) : null));
};
//# sourceMappingURL=PivotsControl.js.map